/**
 * MediaUploadField Component
 *
 * A theme-aware media upload field with side preview and modal expansion.
 * Designed for images and videos with click-to-expand functionality.
 *
 * Features:
 * - Side-by-side layout: TextField + Preview Box
 * - Preview matches TextField height
 * - Click preview to expand to full-size modal
 * - Drag-and-drop file upload support
 * - File validation (type and size)
 * - URL validation and loading states
 * - Material-UI v7 integration
 * - Theme-only styling (NO hardcoded values)
 *
 * @example
 * ```tsx
 * <MediaUploadField
 *   value={imageFile}
 *   onChange={setImageFile}
 *   mediaType="image"
 *   label="Upload Image"
 *   showPreview={true}
 * />
 * ```
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  useTheme,
  FormHelperText,
  Tooltip,
  CircularProgress,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  VideoLibrary as VideoIcon,
  BrokenImage as BrokenImageIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import type { MediaUploadFieldProps } from './MediaUploadField.types';

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_MAX_FILE_SIZE_MB = {
  image: 10,
  // Videos >20MB go direct-to-GCS via signed URLs (OnboardingMediaUploadService
  // routing), so the ceiling is the signed-URL contract cap, not Cloud Run.
  // NB: a 2GB MP4 means a very long download for funnel visitors.
  video: 2048,
};

const DEFAULT_ACCEPTED_TYPES = {
  image: ['image/*'],
  video: ['video/*'],
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getFilePreviewUrl = (file: File): string => {
  return URL.createObjectURL(file);
};

const validateFileSize = (file: File, maxSizeMB: number): string | null => {
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    return `File size exceeds ${maxSizeMB}MB limit`;
  }
  return null;
};

const validateFileType = (file: File, acceptedTypes: string[]): string | null => {
  const fileType = file.type;
  const isAccepted = acceptedTypes.some(type => {
    if (type.endsWith('/*')) {
      const category = type.split('/')[0];
      return fileType.startsWith(category + '/');
    }
    return fileType === type;
  });

  if (!isAccepted) {
    return `File type ${fileType} is not accepted`;
  }
  return null;
};

const getFileSizeDisplay = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(0)}KB` : `${mb.toFixed(2)}MB`;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const MediaUploadField = React.memo<MediaUploadFieldProps>(({
  // Core props
  value,
  onChange,

  // Media configuration
  mediaType,
  label,
  helperText,
  placeholder = 'Enter URL or upload file',

  // File validation
  maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB[mediaType],
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES[mediaType],

  // UI configuration
  showPreview = true,
  disabled = false,
  allowPreviewWhenDisabled = false,
  required = false,
  error = false,
  errorMessage,
  size = 'medium',

  // Styling
  fullWidth = true,

  // Callbacks
  onError,

  // Testing
  inputTestId,
  uploadButtonTestId = 'CloudUploadIcon',
  previewTestId,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textFieldRef = useRef<HTMLDivElement>(null);

  // ============================================================================
  // STATE
  // ============================================================================

  const [urlValue, setUrlValue] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Update when value prop changes (including after upload completes)
  useEffect(() => {
    if (typeof value === 'string' && value) {
      setUrlValue(value);
      setPreviewUrl(value);
      validateRemoteUrl(value);
    } else if (value instanceof File) {
      setUrlValue(value.name);
      setPreviewUrl(getFilePreviewUrl(value));
      setPreviewError(false);
    } else if (value === null || value === undefined) {
      // Clear state when value is reset
      setUrlValue('');
      setPreviewUrl(null);
      setPreviewError(false);
      setIsLoadingPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const validateRemoteUrl = (url: string) => {
    if (!url.trim()) return;

    setIsLoadingPreview(true);
    setPreviewError(false);

    if (mediaType === 'image') {
      // For images, use Image object with onload
      const img = new Image();

      img.onload = () => {
        setIsLoadingPreview(false);
        setPreviewError(false);
      };

      img.onerror = () => {
        setIsLoadingPreview(false);
        setPreviewError(true);
      };

      img.src = url;
    } else {
      // For videos, use video element with loadedmetadata event
      const video = document.createElement('video');

      video.onloadedmetadata = () => {
        setIsLoadingPreview(false);
        setPreviewError(false);
      };

      video.onerror = () => {
        setIsLoadingPreview(false);
        setPreviewError(true);
      };

      // Set preload to metadata only (don't download entire video)
      video.preload = 'metadata';
      video.src = url;
    }
  };

  const handleFileSelect = (file: File) => {
    setInternalError(null);
    setPreviewError(false);

    // Validate file type
    const typeError = validateFileType(file, acceptedFileTypes);
    if (typeError) {
      setInternalError(typeError);
      onError?.(typeError);
      return;
    }

    // Validate file size
    const sizeError = validateFileSize(file, maxFileSizeMB);
    if (sizeError) {
      setInternalError(sizeError);
      onError?.(sizeError);
      return;
    }

    // Clean up old preview URL
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }

    // Set new file and preview
    const newPreviewUrl = getFilePreviewUrl(file);
    setPreviewUrl(newPreviewUrl);
    setUrlValue(file.name);
    onChange(file);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = event.target.value;
    setUrlValue(newUrl);
    setInternalError(null);

    // Only process if it's a URL (not a filename from file upload)
    if (!(value instanceof File)) {
      if (newUrl.trim()) {
        setPreviewUrl(newUrl);
        onChange(newUrl);
        validateRemoteUrl(newUrl);
      } else {
        setPreviewUrl(null);
        onChange(null);
        setPreviewError(false);
        setIsLoadingPreview(false);
      }
    }
  };

  const handleDelete = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setUrlValue('');
    setInternalError(null);
    setPreviewError(false);
    setIsLoadingPreview(false);
    onChange(null);
    setModalOpen(false);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handlePreviewClick = () => {
    if ((allowPreviewWhenDisabled || !disabled) && previewUrl && !isLoadingPreview) {
      setModalOpen(true);
    }
  };

  // ============================================================================
  // DRAG AND DROP HANDLERS
  // ============================================================================

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const hasValue = Boolean(value);
  const displayError = error || Boolean(internalError);
  const displayErrorMessage = errorMessage || internalError;
  const isFile = value instanceof File;
  const isEditable = !isFile; // URL field is editable only when not showing filename
  const canOpenPreview = Boolean(previewUrl && !isLoadingPreview && (!disabled || allowPreviewWhenDisabled));

  // Get TextField height for preview sizing
  const textFieldHeight = size === 'small' ? 40 : 56;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
      }}
    >
      {/* Label */}
      {label && (
        <Typography
          variant="body2"
          sx={{
            color: displayError
              ? theme.palette.error.main
              : theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          {label}
          {required && (
            <Typography component="span" sx={{ color: theme.palette.error.main }}>
              {' *'}
            </Typography>
          )}
        </Typography>
      )}

      {/* Horizontal Row: TextField + Preview */}
      <Box sx={{ display: 'flex', gap: theme.spacing(1.5), alignItems: 'stretch' }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFileTypes.join(',')}
          onChange={handleFileInputChange}
          disabled={disabled}
          style={{ display: 'none' }}
        />

        {/* TextField + Upload Button */}
        <Box sx={{ flex: 1 }}>
          <TextField
            ref={textFieldRef}
            value={urlValue}
            onChange={handleUrlChange}
            placeholder={placeholder}
            disabled={disabled || !isEditable}
            error={displayError}
            fullWidth
            size={size}
            slotProps={{
              input: {
                inputProps: {
                  'data-testid': inputTestId,
                },
                endAdornment: (
                  <Tooltip
                    title={hasValue && isFile ? 'Change file' : 'Upload file'}
                    placement="top"
                  >
                    <span>
                      <IconButton
                        onClick={handleUploadClick}
                        disabled={disabled}
                        size={size}
                        sx={{
                          color: hasValue && isFile
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: alpha(
                              hasValue && isFile
                                ? theme.palette.success.main
                                : theme.palette.primary.main,
                              0.08
                            ),
                          },
                          transition: theme.transitions.create(['color', 'background-color'], {
                            duration: theme.transitions.duration.shortest,
                          }),
                        }}
                      >
                        <UploadIcon data-testid={uploadButtonTestId} fontSize={size === 'small' ? 'small' : 'medium'} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ),
              },
            }}
          />
        </Box>

        {/* Preview Box (to the right, matches TextField height) */}
        {showPreview && hasValue && previewUrl && (
          <Tooltip title={canOpenPreview ? 'Click to view full size' : ''} placement="top">
            <Box
              data-testid={previewTestId}
              onClick={handlePreviewClick}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                position: 'relative',
                width: textFieldHeight, // Square aspect ratio
                height: textFieldHeight,
                borderRadius: `${typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius : 4}px`,
                border: isDragging
                  ? `2px dashed ${theme.palette.primary.main}`
                  : `1px solid ${theme.palette.divider}`,
                backgroundColor: isDragging
                  ? alpha(theme.palette.primary.main, 0.05)
                  : 'transparent',
                overflow: 'visible',
                cursor: canOpenPreview ? 'pointer' : 'default',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: theme.transitions.create(['box-shadow', 'border-color', 'background-color'], {
                  duration: theme.transitions.duration.shortest,
                }),
                '&:hover': canOpenPreview ? {
                  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.12)}`,
                  borderColor: theme.palette.primary.main,
                } : undefined,
              }}
            >
              {/* Loading State */}
              {isLoadingPreview && (
                <CircularProgress size={textFieldHeight * 0.5} />
              )}

              {/* Error State */}
              {!isLoadingPreview && previewError && (
                <BrokenImageIcon
                  sx={{
                    fontSize: textFieldHeight * 0.6,
                    color: theme.palette.error.main,
                  }}
                />
              )}

              {/* Preview Content */}
              {!isLoadingPreview && !previewError && (
                <>
                  {mediaType === 'image' ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        overflow: 'hidden',
                        borderRadius: 'inherit',
                      }}
                    >
                      <img
                        src={previewUrl}
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  ) : (
                    <VideoIcon
                      sx={{
                        fontSize: textFieldHeight * 0.6,
                        color: theme.palette.primary.main,
                      }}
                    />
                  )}
                </>
              )}

              {/* X Delete Button Overlay */}
              {!disabled && !isLoadingPreview && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    width: 20,
                    height: 20,
                    padding: 0,
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.secondary,
                    border: `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      backgroundColor: theme.palette.background.paper,
                    },
                    boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.12)}`,
                    transition: theme.transitions.create(['background-color', 'transform'], {
                      duration: theme.transitions.duration.shortest,
                    }),
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              )}

              {/* Drag-and-drop overlay message */}
              {isDragging && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    pointerEvents: 'none',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: '0.65rem',
                    }}
                  >
                    Drop
                  </Typography>
                </Box>
              )}
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Helper Text / Error Message */}
      {(helperText || displayErrorMessage) && (
        <FormHelperText error={displayError}>
          {displayErrorMessage || helperText}
        </FormHelperText>
      )}

      {/* Modal for Full Preview */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={() => setModalOpen(false)}
              size="small"
              aria-label="close dialog"
              sx={{
                color: theme.palette.text.secondary,
                backgroundColor: theme.palette.action.hover,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            variant="h6"
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Media Preview
          </Typography>
          <Box />
        </DialogTitle>

        <DialogContent>
          {/* Large Preview */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: theme.spacing(3),
              backgroundColor: theme.palette.grey[50],
              borderRadius: `${typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius : 4}px`,
            }}
          >
            {mediaType === 'image' ? (
              <img
                src={previewUrl || ''}
                alt="Full preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                  borderRadius: typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius : 4,
                }}
              />
            ) : (
              <video
                src={previewUrl || ''}
                controls
                style={{
                  maxWidth: '100%',
                  maxHeight: 400,
                  borderRadius: typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius : 4,
                }}
              />
            )}
          </Box>

          {/* File Metadata */}
          <Box
            sx={{
              mt: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1),
              p: theme.spacing(2),
              backgroundColor: theme.palette.background.default,
              borderRadius: `${typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius : 4}px`,
            }}
          >
            <Typography variant="body2">
              <strong>Filename:</strong>{' '}
              {isFile ? (value as File).name : 'Remote URL'}
            </Typography>
            {isFile && (
              <>
                <Typography variant="body2">
                  <strong>Size:</strong>{' '}
                  {getFileSizeDisplay((value as File).size)}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong>{' '}
                  {(value as File).type || 'Unknown'}
                </Typography>
              </>
            )}
            <Typography variant="body2">
              <strong>Format:</strong>{' '}
              {mediaType === 'image' ? 'Image' : 'Video'}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleDelete}
            color="error"
            startIcon={<DeleteIcon />}
            disabled={disabled}
          >
            Delete {mediaType === 'image' ? 'Image' : 'Video'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setModalOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

// ============================================================================
// COMPONENT METADATA
// ============================================================================

MediaUploadField.displayName = 'MediaUploadField';

// ============================================================================
// EXPORTS
// ============================================================================

export default MediaUploadField;
